import { Validators } from '@angular/forms';
import {
    IAlphanumericCellEditorParameters,
    IBooleanCellRendererParameters,
    IComboCellEditorParameters,
    IConfigColumnGridControl,
    IConfigControlGrid,
    IControlType,
    IDataGridControlType,
    IDataSource,
    IDataSourceCellEditorParameters,
    IDateCellEditorParameters,
    IGridControl,
    IGridSelectionType,
    INumericCellEditorParameters,
    IVisibilityStatus,
} from '@se/cp.common';
import {
    DataBinding,
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
} from '@se/se.platform.base';
import {
    AlphanumericCellEditorComponent,
    AutocompleteCellEditorComponent,
    BooleanCellRendererComponent,
    ButtonCellRendererComponent,
    ComboCellEditorComponent,
    DateCellEditorComponent,
    DateCellRendererComponent,
    DateFormatEnum,
    DateInputEnum,
    EllipsisWithTooltipCellRendererComponent,
    IAlphanumericCellEditorParams,
    IAutocompleteCellEditorParams,
    IBooleanCellRendererParams,
    IComboCelleEditorParams,
    IDateCellEditorParams,
    IDateCellRendererParams,
    ILetterCellEditorParams,
    IMaskedDateCellEditorParams,
    INumericCellEditorParams,
    LetterCellEditorComponent,
    MaskedDateCellEditorComponent,
    MultiIconCellRendererComponent,
    NumericCellEditorComponent,
} from '@se/ui.datagrid';
import { ColDef, ICellRendererParams, NewValueParams, RowSelectionOptions, ValueSetterParams } from 'ag-grid-community';
import { isNil, last, noop, sortBy } from 'lodash-es';

import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { rowProportional } from '../utils/utils';
import { AdditionalContentControlType, createAdditionalContent } from './cp-additional-content';
import { CpControl } from './cp-control';
import { CpEntity } from './cp-entity';

export class CpGridControl extends CpControl {
    readonly controlTypeGroup = 'Grid';
    readonly dataBinding: DataBinding;

    /** Grid Options */
    colResizeDefault: 'shift' | undefined = 'shift';
    columnDefs: ColDef[] = [];
    columnsConfigs: IConfigColumnGridControl[] = [];
    gridComponents: Record<string, any> = {};
    enableBrowserTooltips: boolean = true;
    pagination: boolean = false;
    paginationAutoPageSize: boolean = false;
    rowSelection?: RowSelectionOptions;
    singleClickEdit: boolean = true;
    tooltipShowDelay: number = 500;
    stopEditingWhenCellsLoseFocus: boolean = true; // Commentare per debuggare gli editor
    suppressNoRowsOverlay: boolean = true;
    suppressScrollOnNewData: boolean = true;

    /** ------ */

    additionalContent: AdditionalContentControlType[];
    control: IGridControl;
    dataSourceService?: CpDatasourceService;
    configuratorService?: SeConstraintConfiguratorService;
    constraintFilterService?: SeConstraintApplierService;
    constraintNotifier?: SeConstraintNotifierService;

    private _firstEditableColumnIndex: number | null = null;
    maskedDateFormat?: string;
    maskedTimeFormat?: string;
    maskedDateTimeFormat?: string;
    maskedDateSeparator?: string;
    maskedTimeSeparator?: string;
    separatorInUse?: string;

    private modelService!: CpModelService;

    private isFilterActive: boolean = false;

    constructor(control: IGridControl, dataBindingContext?: DataBinding) {
        super(control);
        this.control = control;
        this.dataBinding = new DataBinding(control.config[0].columnConfigArray[0].dataBinding, dataBindingContext);

        this.configureGridOptions(control.config[0]);
        this.createColumns(control.config[0]);
        this.addCustomColumns(control.config[0].customColumnsConfig ?? []);

        this.additionalContent = createAdditionalContent(control.additionalContent ?? [], dataBindingContext);
    }

    get isGridEditable() {
        return !this.control.config[0].readonly;
    }

    get firstEditableColumnIndex() {
        if (this._firstEditableColumnIndex !== null) {
            return this._firstEditableColumnIndex;
        }
        return null;
    }

    canAlignToBottom() {
        return true;
    }

    getHeightHint() {
        return rowProportional(this.rowSpan);
    }

    setDataSource: (idDataSource: string, useObject: boolean) => IDataSource | Array<string> = (
        _idDataSource: string,
        _useObject: boolean,
    ) => [];

    setHeight(height: number) {
        this.height = height;
    }

    setModelService(modelService: CpModelService) {
        this.modelService = modelService;
    }

    removeRow: (cellRendererParams: ICellRendererParams, icon: string) => void = noop;

    addRow: () => void = noop;

    removeAllRows: () => void = noop;

    private createColumns(control: IConfigControlGrid) {
        this.columnsConfigs = sortBy(control.columnConfigArray, 'order');
        this.isFilterActive = false;
        this.columnDefs = this.columnsConfigs.map((c, i) => this.columnConfigToColDef(control, c, i));

        if (this.control.controlType !== IControlType.DataGridNoAddDelete) {
            // Define action colum with add new row e delete row
            this.addActionButtonColumn();
        }

        if (control.readonly) {
            this._firstEditableColumnIndex = null;
        }
    }

    private addCustomColumns(columns: ColDef[]) {
        this.columnDefs.push(...(columns ?? []));
    }

    private columnConfigToColDef(control: IConfigControlGrid, c: IConfigColumnGridControl, colIndex: number): ColDef {
        if (
            this._firstEditableColumnIndex === null &&
            !c.readOnly &&
            c.visibilityStatus === IVisibilityStatus.VISIBLE
        ) {
            this._firstEditableColumnIndex = colIndex;
        }
        const currentCol: ColDef = {
            headerName: c.required ? '*' + c.title : c.title,
            headerTooltip: c.title,
            suppressHeaderKeyboardEvent: () => true,
            hide: c.visibilityStatus !== null ? !c.visibilityStatus : false,
            editable: () => this.isGridEditable && !c.readOnly,
            minWidth: c.desiredSize || 100,
            width: c.desiredSize || undefined,
            // autoHeight: true,

            sortable: !control.disableSort && c.sortable,
            sort: c.sort && c.sort.default !== 'NONE' ? (c.sort.default.toLocaleLowerCase() as any) : undefined,
            resizable: true,
            filter: false,
            filterParams: {
                suppressAndOrCondition: true,
            },
            colId: c.dataBinding,
            field: c.dataBinding.split('.').pop(),
            cellEditorParams: {
                inlineEditor: control.inlineEditor ?? true,
                required: c.required || null,
            },
            cellRendererParams: {
                inlineEditor: control.inlineEditor ?? true,
                required: c.required || null,
            },
            cellEditorPopup: !(control.inlineEditor ?? true),
            onCellValueChanged: (event: NewValueParams) => {
                /** clear sort */
                event.api.applyColumnState({
                    defaultState: { sort: null },
                });
            },
        };

        // ----- VALIDATION
        if (c.required) {
            (currentCol as any).validators = [Validators.required];
        } else {
            (currentCol as any).validators = [];
        }
        // ------
        this.controlTypeConfig(c, currentCol);
        return currentCol;
    }

    private controlTypeConfig(colConfig: IConfigColumnGridControl, colDef: ColDef) {
        const property = last(colConfig.dataBinding.split('.'))!;

        switch (colConfig.controlType) {
            case IDataGridControlType.AlphanumericCellEditor: {
                // 151 ALPHANUMERIC
                this.defineValueSetterGetter(colDef, property);
                colDef.cellEditor = this.gridComponents.alphanumericCellEditor;
                const cellEditorParams: IAlphanumericCellEditorParams = {
                    required: colDef.cellRendererParams.required,
                    inlineEditor: colDef.cellRendererParams.inlineEditor,
                    notAdmissibleChars: (colConfig.controlParameters as IAlphanumericCellEditorParameters)
                        ?.notAdmissibleChars,
                };
                colDef.cellEditorParams = cellEditorParams;
                colDef.cellRenderer = this.gridComponents.ellipsisWithTooltipCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agTextColumnFilter';
                    colDef.filterParams.filterOptions = ['equals', 'notEqual', 'contains', 'notContains'];
                }
                break;
            }
            case IDataGridControlType.LetterCellEditor: {
                // 155 LETTER
                this.defineValueSetterGetter(colDef, property);
                colDef.cellEditor = this.gridComponents.letterCellEditor;
                const cellEditorParams: ILetterCellEditorParams = {
                    required: colDef.cellRendererParams.required,
                    inlineEditor: colDef.cellRendererParams.inlineEditor,
                    notAdmissibleChars: (colConfig.controlParameters as IAlphanumericCellEditorParameters)
                        ?.notAdmissibleChars,
                };
                colDef.cellEditorParams = cellEditorParams;
                colDef.cellRenderer = this.gridComponents.ellipsisWithTooltipCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agTextColumnFilter';
                    colDef.filterParams.filterOptions = ['equals', 'notEqual', 'contains', 'notContains'];
                }
                break;
            }
            case IDataGridControlType.NumericCellEditor: {
                // 156 NUMERIC
                this.defineValueSetterGetter(colDef, property);
                colDef.cellEditor = this.gridComponents.numericCellEditor;
                if (colConfig.controlParameters) {
                    const cellParams: INumericCellEditorParameters =
                        colConfig.controlParameters as INumericCellEditorParameters;
                    colDef.cellEditorParams = (): INumericCellEditorParams => {
                        return {
                            required: colDef.cellRendererParams.required,
                            inlineEditor: colDef.cellRendererParams.inlineEditor,
                            min: isNil(cellParams.min) ? undefined : cellParams.min,
                            max: isNil(cellParams.max) ? undefined : cellParams.max,
                            decimal: isNil(cellParams.decimal) ? undefined : cellParams.decimal,
                            separatorInUse: this.separatorInUse,
                        };
                    };
                }
                colDef.cellRenderer = this.gridComponents.ellipsisWithTooltipCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agNumberColumnFilter';
                    colDef.filterParams.filterOptions = [
                        'equals',
                        'notEqual',
                        'lessThan',
                        'lessThanOrEqual',
                        'greaterThan',
                        'greaterThanOrEqual',
                        'inRange',
                    ];
                }
                break;
            }
            case IDataGridControlType.BooleanCellRenderer: {
                // 152 BOOLEAN
                this.defineValueSetterGetter(colDef, property);
                colDef.cellRenderer = this.gridComponents.booleanCellRenderer;
                colDef.editable = false;
                const cellRendererParams: IBooleanCellRendererParams = {
                    required: colDef.cellRendererParams.required,
                    disabled: !this.isGridEditable || colConfig.readOnly,
                    color: (colConfig.controlParameters as IBooleanCellRendererParameters).color,
                    editor: (colConfig.controlParameters as IBooleanCellRendererParameters).editor,
                    isEditor: false,
                };
                colDef.cellRendererParams = cellRendererParams;
                colDef.filter = false;
                break;
            }
            case IDataGridControlType.DateCellEditor: {
                // 154 DATE
                this.defineValueSetterGetterDateTime(colDef, property);
                colDef.cellEditor = this.gridComponents.dateCellEditor;
                if (colConfig.controlParameters) {
                    const dateParams: IDateCellEditorParameters =
                        colConfig.controlParameters as IDateCellEditorParameters;
                    colDef.cellEditorParams = (): IDateCellEditorParams => {
                        return {
                            required: colDef.cellRendererParams.required,
                            inlineEditor: colDef.cellRendererParams.inlineEditor,
                            inputType: dateParams.inputType || null,
                            inputFormat: dateParams.inputFormat || null,
                            dateInputFormat: this.maskedDateFormat,
                            dateTimeInputFormat: this.maskedDateTimeFormat,
                            timeInputFormat: this.maskedTimeFormat,
                            dateSeparator: this.maskedDateSeparator,
                            timeSeparator: this.maskedTimeSeparator,
                        };
                    };
                    colDef.cellRendererParams = (): IDateCellRendererParams => {
                        return {
                            required: colDef.cellRendererParams.required,
                            inputType: dateParams.inputType ?? DateInputEnum.DATE,
                            inputFormat: dateParams.inputFormat ?? DateFormatEnum.SHORT,
                            dateSeparator: this.maskedDateSeparator,
                            timeSeparator: this.maskedTimeSeparator,
                        };
                    };
                }
                colDef.cellRenderer = this.gridComponents.dateTimeCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agDateColumnFilter';
                    colDef.filterParams.filterOptions = ['equals', 'notEqual', 'inRange'];
                    colDef.filterParams.comparator = this.customDateComparator;
                }
                break;
            }
            case IDataGridControlType.MaskedDateCellEditor: {
                // 157 MaskedDateCellEditor
                this.defineValueSetterGetterDateTime(colDef, property);
                colDef.cellEditor = this.gridComponents.maskedDateCellEditor;
                if (colConfig.controlParameters) {
                    const dateParams: IDateCellEditorParameters =
                        colConfig.controlParameters as IDateCellEditorParameters;
                    colDef.cellEditorParams = (): IMaskedDateCellEditorParams => {
                        return {
                            required: colDef.cellRendererParams.required,
                            inlineEditor: colDef.cellRendererParams.inlineEditor,
                            inputType: dateParams.inputType,
                            dateInputFormat: this.maskedDateFormat,
                            timeInputFormat: this.maskedTimeFormat,
                            dateTimeInputFormat: this.maskedDateTimeFormat,
                            dateSeparator: this.maskedDateSeparator,
                            timeSeparator: this.maskedTimeSeparator,
                        };
                    };
                    colDef.cellRendererParams = (): IDateCellRendererParams => {
                        return {
                            required: colDef.cellRendererParams.required,
                            inputType: dateParams.inputType ?? DateInputEnum.DATE,
                            inputFormat: dateParams.inputFormat ?? DateFormatEnum.SHORT,
                            dateSeparator: this.maskedDateSeparator,
                            timeSeparator: this.maskedTimeSeparator,
                        };
                    };
                }
                colDef.cellRenderer = this.gridComponents.dateTimeCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agDateColumnFilter';
                    colDef.filterParams.filterOptions = ['equals', 'notEqual', 'inRange'];
                    colDef.filterParams.comparator = this.customDateComparator;
                }
                break;
            }

            case IDataGridControlType.ComboCellEditor: {
                //153
                colDef.cellEditor = this.gridComponents.comboCellEditor;
                if (colConfig.controlParameters) {
                    const params: IComboCellEditorParameters =
                        colConfig.controlParameters as IComboCellEditorParameters;
                    const cellEditorParams: IComboCelleEditorParams = {
                        required: colDef.cellRendererParams.required,
                        inlineEditor: colDef.cellRendererParams.inlineEditor,
                        idDataSource: params.idDataSource,
                        dataSource: () => this.setDataSource(params.idDataSource, true),
                        viewValueFieldName: params.viewValueFieldName ?? 'Description',
                        valueFieldName: params.valueFieldName,
                        multi: params.multi,
                        isResettable: !colConfig.required ? (params.isResettable ?? false) : false,
                        // instanze dei servizi delle regole
                        configuratorService: this.configuratorService,
                        constraintFilterService: this.constraintFilterService,
                        constraintNotifier: this.constraintNotifier,
                    };
                    colDef.cellEditorParams = cellEditorParams;
                    colDef.cellRendererParams.viewFieldName = colDef.cellEditorParams.viewValueFieldName;
                    colDef.cellRendererParams.isArray = !!params.multi;
                    const viewValueFieldName = params.viewValueFieldName ?? 'Description';
                    const isArray = params.multi ?? false;
                    colDef.autoHeight = params.multi ? true : false;
                    colDef.comparator = (obj1, obj2) => {
                        return this.setComboObjValue(viewValueFieldName, isArray, obj1).localeCompare(
                            this.setComboObjValue(viewValueFieldName, isArray, obj2),
                        );
                    };
                }
                this.defineValueSetterGetterToJson(colDef, property);
                colDef.cellRenderer = this.gridComponents.ellipsisWithTooltipCellRender;
                colDef.filter = false;
                break;
            }
            case IDataGridControlType.AutocompleteCellEditor: {
                // 158 AUTOCOMPLETE
                this.defineValueSetterGetter(colDef, property);
                colDef.cellEditor = this.gridComponents.autocompleteCellEditor;
                if (colConfig.controlParameters) {
                    let params: IDataSourceCellEditorParameters =
                        colConfig.controlParameters as IDataSourceCellEditorParameters;
                    const cellEditorParams: IAutocompleteCellEditorParams = {
                        required: colDef.cellRendererParams.required,
                        inlineEditor: colDef.cellRendererParams.inlineEditor,
                        dataSource: () => this.setDataSource(params.idDataSource, false),
                    };
                    colDef.cellEditorParams = cellEditorParams;
                }
                colDef.cellRenderer = this.gridComponents.ellipsisWithTooltipCellRender;
                if (this.isFilterActive) {
                    colDef.filter = 'agTextColumnFilter';
                    colDef.filterParams.filterOptions = ['equals', 'notEqual', 'contains', 'notContains'];
                }
                break;
            }
            default:
                this.defineValueSetterGetter(colDef, property);
        }
    }

    private setComboObjValue(viewFieldName: string, isArray: boolean, obj: any) {
        if (isArray) {
            return obj ? obj.map((elem: any) => elem[viewFieldName]).toString() : '';
        } else {
            return obj ? obj[viewFieldName] : '';
        }
    }

    private defineValueSetterGetter(colDef: ColDef, property: string) {
        /*  colDef.valueGetter = (params: ValueGetterParams) => {
            const data: CpEntity = params.data;
            return data.getValue(new DataBinding(property));
        }; */
        colDef.valueSetter = (params: ValueSetterParams) => {
            const data: CpEntity = params.data.entity;
            const binding = data.path.addSimple(property);
            this.modelService.setValue(binding, params.newValue);
            return true;
        };
    }

    private defineValueSetterGetterDateTime(colDef: ColDef, property: string) {
        /* colDef.valueGetter = (params: ValueGetterParams) => {
            const data: CpEntity = params.data;
            return data.getValue(new DataBinding(property));
        }; */
        colDef.valueSetter = (params: ValueSetterParams) => {
            const data: CpEntity = params.data.entity;
            const binding = data.path.addSimple(property);
            this.modelService.setValue(binding, params.newValue ?? null);
            return true;
        };
    }

    private defineValueSetterGetterToJson(colDef: ColDef, property: string) {
        /* colDef.valueGetter = (params: ValueGetterParams) => {
            const data: CpEntity = params.data;
            const entity: CpEntity = data.getValue(new DataBinding(property)) as CpEntity;
            if (typeof entity?.toJSON === 'function') {
                // safe to use the function
                return entity?.toJSON();
            }
            return entity;
        }; */
        colDef.valueSetter = (params: ValueSetterParams) => {
            const parent: CpEntity = params.data.entity;
            const newValueBinding = parent.path.addSimple(property);
            this.modelService.setValue(newValueBinding, params.newValue);
            return true;
        };
    }

    private configureGridOptions(control: IConfigControlGrid) {
        this.gridComponents = {
            numericCellEditor: NumericCellEditorComponent,
            letterCellEditor: LetterCellEditorComponent,
            alphanumericCellEditor: AlphanumericCellEditorComponent,
            booleanCellRenderer: BooleanCellRendererComponent,
            dateCellEditor: DateCellEditorComponent,
            maskedDateCellEditor: MaskedDateCellEditorComponent,
            dateTimeCellRender: DateCellRendererComponent,
            comboCellEditor: ComboCellEditorComponent,
            ellipsisWithTooltipCellRender: EllipsisWithTooltipCellRendererComponent,
            buttonCellRenderer: ButtonCellRendererComponent,
            autocompleteCellEditor: AutocompleteCellEditorComponent,
            multiIconCellRenderer: MultiIconCellRendererComponent,
        };
        switch (control.selectionType) {
            case IGridSelectionType.SINGLE:
                {
                    this.rowSelection = {
                        mode: 'singleRow',
                        checkboxes: false,
                        enableClickSelection: true,
                        enableSelectionWithoutKeys: false,
                    };
                }
                break;
            case IGridSelectionType.MULTIPLE:
                {
                    this.rowSelection = {
                        mode: 'multiRow',
                        checkboxes: false,
                        headerCheckbox: false,
                        enableClickSelection: true,
                        enableSelectionWithoutKeys: false,
                    };
                }
                break;
            default: {
                this.rowSelection = {
                    mode: 'singleRow',
                    checkboxes: false,
                    enableClickSelection: false,
                    enableSelectionWithoutKeys: false,
                };
            }
        }
        this.singleClickEdit = control.singleClickEdit ?? true;
    }

    private addActionButtonColumn() {
        this.columnDefs.push({
            cellRenderer: this.gridComponents.buttonCellRenderer,
            cellRendererParams: () => {
                return {
                    onClick: this.removeRow,
                    matIcon: 'delete',
                    tooltip: 'DeleteRow',
                    activeClass: true,
                };
            },
            sortable: false,
            resizable: false,
            editable: false,
            suppressSizeToFit: true,
            pinned: 'right',
            filter: undefined,
            width: 50,
            cellClass: 'cp-grid-action-button-col-cell',
            headerComponent: this.gridComponents.buttonCellRenderer,
            headerComponentParams: () => {
                return {
                    onClick: this.addRow,
                    matIcon: 'playlist_add',
                    tooltip: 'AddRow',
                };
            },
            headerClass: 'cp-grid-action-button-col-header',
            suppressHeaderKeyboardEvent: () => true,
            suppressMovable: true,
        });
    }

    private customDateComparator(filterLocalDateAtMidnight: Date, cellValue: string) {
        if (cellValue === null) {
            return 0;
        }
        const cellDate = new Date(cellValue);
        if (cellDate < filterLocalDateAtMidnight) {
            return -1;
        } else if (cellDate > filterLocalDateAtMidnight) {
            return 1;
        } else {
            return 0;
        }
    }
}
